import { AppState, Auth0Context, Auth0Provider, initialContext } from '@auth0/auth0-react';
import { QueryClientProvider } from '@tanstack/react-query';
import React, { useEffect, type JSX } from 'react';
import Router, { resolvePostLoginRedirect } from './Router';
import { AuthStateMonitor } from './components/common/AuthStateMonitor';
import ChunkErrorBoundary from './components/common/ChunkErrorBoundary';
import ErrorReporter from './components/common/ErrorReporter';
import { ForumListProvider } from './components/common/ForumListContext';
import { ReactQueryDevtoolsGate } from './components/common/ReactQueryDevtoolsGate';
import { ToastProvider, WebSocketProvider } from '@eptai/eptai-app-components';
import {
  NavigationProvider,
  ThemeProvider,
  AppAuthProvider,
  FeatureFlagsProvider,
  AnnouncementsProvider,
  NotificationsProvider,
  useAppAuth,
} from './contexts';
import {
  apiAudience,
  auth0ClientId,
  auth0Domain,
  isSalesforceEmbedded,
  usesEmulatorAuth,
  websocketPath,
} from './config';
import { cleanupAuthParams } from './utils/authUtils';
import { controlLoginPath, peekMcpControlLogin } from './utils/mcpControlLogin';
import { deliverMcpReturn, mcpClientReturnUrl } from './utils/mcpOAuthHandoff';
import { decodeJwtSegment } from './utils/localAuth';
import { prefetchSidebarPages } from './utils/prefetchSidebarPages';
import { queryClient } from './utils/queryClient';

// =============================================================================
// EMULATOR-AUTH-ONLY: Production Auth0 for WebSocket.
// The production WebSocket (wss://ws.api.ept.ai) requires real Auth0 tokens.
// With emulator auth the app uses local JWTs for API calls, so a separate
// production Auth0 client is initialised here to obtain a valid WS token.
// When VITE_REMOTE_BACKEND is set (or on hosted apps) this path is skipped.
// =============================================================================
const PROD_AUTH0_DOMAIN = 'login.ept.ai';
const PROD_AUTH0_CLIENT_ID = 'RXyxcWkLx7y2Xcw92lr3yrXKUINXj0o4';

type WebsocketTokenClaims = {
  __raw?: string;
  __id_raw?: string;
};

// WebSocket wrapper component that provides WebSocket context using the component library
const WebSocketWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getAccessTokenSilently, getIdTokenClaims, user } = useAppAuth();
  const isLocal = usesEmulatorAuth();

  const [prodAuth0Client, setProdAuth0Client] = React.useState<{
    getIdTokenClaims: () => Promise<{ __raw?: string } | null>;
    isAuthenticated: () => Promise<boolean>;
    loginWithPopup: (opts: { authorizationParams: { redirect_uri: string } }) => Promise<void>;
  } | null>(null);
  const [wsAuthReady, setWsAuthReady] = React.useState(!isLocal);
  const [authAttempted, setAuthAttempted] = React.useState(false);

  React.useEffect(() => {
    console.log('WSDBG_APP_MOUNT_v1', {
      isLocal,
      hasUser: Boolean(user),
      href: typeof window === 'undefined' ? '' : window.location.href,
    });
  }, [isLocal, user]);

  const isTokenExpired = (token: string): boolean => {
    try {
      const payload = JSON.parse(decodeJwtSegment(token.split('.')[1])) as { exp?: number };
      return (payload.exp ?? 0) < Math.floor(Date.now() / 1000);
    } catch {
      return true;
    }
  };

  // LOCALHOST-ONLY: Initialise production Auth0 client and authenticate once
  React.useEffect(() => {
    if (!isLocal) return;
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') return;
    if (authAttempted) return;

    console.log('[WEBSOCKET] LOCALHOST: Initializing production Auth0 client for WebSocket...');
    setAuthAttempted(true);

    import('@auth0/auth0-spa-js')
      .then(async ({ Auth0Client }) => {
        const client = new Auth0Client({
          domain: PROD_AUTH0_DOMAIN,
          clientId: PROD_AUTH0_CLIENT_ID,
          authorizationParams: { redirect_uri: window.location.origin },
          cacheLocation: 'localstorage',
          useRefreshTokens: true,
        });
        setProdAuth0Client(client);

        try {
          const isAuth = await client.isAuthenticated();
          if (isAuth) {
            try {
              const claims = await client.getIdTokenClaims();
              if (claims?.__raw && !isTokenExpired(claims.__raw)) {
                console.log('[WEBSOCKET] LOCALHOST: Cached production token is valid');
                setWsAuthReady(true);
                return;
              }
            } catch (_e) {
              console.log('[WEBSOCKET] LOCALHOST: Cached token expired or invalid');
            }
          }

          console.log('[WEBSOCKET] LOCALHOST: Attempting login popup (one-time)...');
          try {
            await client.loginWithPopup({ authorizationParams: { redirect_uri: window.location.origin } });
            if (await client.isAuthenticated()) {
              console.log('[WEBSOCKET] LOCALHOST: Login successful');
              setWsAuthReady(true);
            } else {
              console.warn('[WEBSOCKET] LOCALHOST: Popup completed but not authenticated');
              setWsAuthReady(false);
            }
          } catch (popupError: unknown) {
            const msg = popupError instanceof Error ? popupError.message : String(popupError);
            console.warn('[WEBSOCKET] LOCALHOST: Login popup failed (likely blocked):', msg);
            setWsAuthReady(false);
          }
        } catch (e) {
          console.error('[WEBSOCKET] LOCALHOST: Auth error:', e);
          setWsAuthReady(false);
        }
      })
      .catch(err => {
        console.error('[WEBSOCKET] LOCALHOST: Failed to import Auth0Client:', err);
        setWsAuthReady(false);
      });
  }, [isLocal, authAttempted]);

  const buildWebsocketUrl = async (): Promise<string> => {
    let accessToken: string | undefined;
    let userAccessToken: string | undefined;
    const hasAuthCallbackParams =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).has('code') &&
      new URLSearchParams(window.location.search).has('state');

    if (isLocal) {
      if (!prodAuth0Client) throw new Error('Production Auth0 client not ready');
      const claims = await prodAuth0Client.getIdTokenClaims();
      if (claims?.__raw && !isTokenExpired(claims.__raw)) {
        accessToken = claims.__raw;
        userAccessToken = claims.__raw;
        console.log('[WEBSOCKET] LOCALHOST: Using production Auth0 token');
      } else {
        throw new Error('Token expired - please refresh page');
      }
    } else {
      const tokenClaims = (await getIdTokenClaims()) as WebsocketTokenClaims | null;
      if (tokenClaims?.__id_raw) {
        accessToken = await getAccessTokenSilently();
        userAccessToken = tokenClaims.__id_raw;
      } else {
        accessToken = tokenClaims?.__raw;
        userAccessToken = tokenClaims?.__raw;
      }
      console.log('[WEBSOCKET][DEBUG] token availability', {
        hasAuthCallbackParams,
        hasAccessToken: Boolean(accessToken),
        hasUserAccessToken: Boolean(userAccessToken),
        hasClaimsRaw: Boolean(tokenClaims?.__raw),
        hasClaimsIdRaw: Boolean(tokenClaims?.__id_raw),
        pathname: typeof window === 'undefined' ? '' : window.location.pathname,
      });
    }

    if (!accessToken || isTokenExpired(accessToken)) {
      throw new Error('No access token available for WebSocket');
    }

    if (!userAccessToken || isTokenExpired(userAccessToken)) {
      throw new Error('No identity token available for WebSocket');
    }

    return `${websocketPath()}?access_token=${accessToken}&user_access_token=${userAccessToken}&client_key=ODY8mXQS82`;
  };

  const getAccessToken = async (): Promise<string | null> => {
    if (isLocal) {
      if (!prodAuth0Client) return null;
      try {
        const claims = await prodAuth0Client.getIdTokenClaims();
        const token = claims?.__raw;
        return token && !isTokenExpired(token) ? token : null;
      } catch {
        return null;
      }
    }

    try {
      const tokenClaims = (await getIdTokenClaims()) as WebsocketTokenClaims | null;
      const token = tokenClaims?.__id_raw ? await getAccessTokenSilently() : tokenClaims?.__raw;
      return token && !isTokenExpired(token) ? token : null;
    } catch {
      return null;
    }
  };

  // Remote-backend on localhost already authenticated via host Auth0 — skip the
  // package's second Auth0 popup. Prop is ignored by older package builds;
  // local USE_LOCAL_PACKAGE dist includes it.
  const remoteLocalhostWsProps = {
    useProvidedAuthOnLocalhost: !isLocal,
  } as { useProvidedAuthOnLocalhost?: boolean };

  return (
    <WebSocketProvider
      websocketUrl={buildWebsocketUrl}
      getAccessToken={getAccessToken}
      enabled={!!user && (wsAuthReady || !isLocal)}
      {...remoteLocalhostWsProps}
    >
      {children}
    </WebSocketProvider>
  );
};

// Inner app content that uses auth context
function McpReturn({ url }: { url: string }): JSX.Element {
  const started = React.useRef(false);
  if (!started.current) {
    started.current = true;
    deliverMcpReturn(url);
  }
  return <></>;
}

/** Starts the sidebar-page downloads once, after the user is logged in. */
function PrefetchSidebarPages(): null {
  const { isAuthenticated, isLoading } = useAppAuth();
  const started = React.useRef(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated || started.current) {
      return;
    }
    started.current = true;
    void prefetchSidebarPages();
  }, [isAuthenticated, isLoading]);

  return null;
}

function AppContent(): JSX.Element {
  const mcpReturn = mcpClientReturnUrl(window.location.search);
  if (mcpReturn) {
    return <McpReturn url={mcpReturn} />;
  }

  return (
    <ToastProvider>
      <AppAuthProvider>
        <FeatureFlagsProvider>
          <AnnouncementsProvider>
            <AuthStateMonitor />
            <NavigationProvider>
              <ForumListProvider>
                <WebSocketWrapper>
                  <NotificationsProvider>
                    <ErrorReporter />
                    <PrefetchSidebarPages />
                    <ChunkErrorBoundary>
                      <Router />
                    </ChunkErrorBoundary>
                  </NotificationsProvider>
                </WebSocketWrapper>
              </ForumListProvider>
            </NavigationProvider>
          </AnnouncementsProvider>
        </FeatureFlagsProvider>
      </AppAuthProvider>
    </ToastProvider>
  );
}

function App(): JSX.Element {
  const isLocal = usesEmulatorAuth();
  const isSalesforceCanvas = !isLocal && isSalesforceEmbedded();
  const client_id = auth0ClientId();
  const auth0_domain = auth0Domain();
  const audience = apiAudience();

  // Log environment mode
  useEffect(() => {
    if (isLocal) {
      console.log('[APP] Running in LOCAL mode - using local API Gateway emulator');
      console.log('[APP] API: http://localhost:8001/v4');
      console.log('[APP] Token endpoint: http://localhost:8001/local-token');
    } else if (isSalesforceCanvas) {
      console.log('[APP] Running in SALESFORCE CANVAS mode - bypassing Auth0Provider');
      console.log('[APP][CANVAS DEBUG] href=', window.location.href);
      console.log('[APP][CANVAS DEBUG] document.referrer=', document.referrer);
      console.log('[APP][CANVAS DEBUG] cookie=', document.cookie);
      console.log('[APP][CANVAS DEBUG] sessionStorage.ept_embedded_mode=', sessionStorage.getItem('ept_embedded_mode'));
      console.log(
        '[APP][CANVAS DEBUG] query params=',
        Object.fromEntries(new URLSearchParams(window.location.search).entries())
      );
    } else {
      console.log('[APP] Running with Auth0 against remote API (or hosted app)');
    }
  }, [isLocal, isSalesforceCanvas]);

  useEffect(() => {
    if (!isSalesforceCanvas) return;

    const originalFetch = window.fetch.bind(window);
    const errorListener = (event: ErrorEvent): void => {
      console.error('[APP][CANVAS DEBUG] window.error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
      });
    };
    const rejectionListener = (event: PromiseRejectionEvent): void => {
      console.error('[APP][CANVAS DEBUG] window.unhandledrejection', event.reason);
    };

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const requestUrl = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
      console.log('[APP][CANVAS DEBUG] fetch start', {
        url: requestUrl,
        method: init?.method || (input instanceof Request ? input.method : 'GET'),
        headers: init?.headers,
        body: typeof init?.body === 'string' ? init.body : init?.body,
      });
      try {
        const response = await originalFetch(input, init);
        const responseClone = response.clone();
        let responseTextPreview = '';
        try {
          responseTextPreview = (await responseClone.text()).slice(0, 1000);
        } catch (readError) {
          responseTextPreview = `[response body unreadable: ${String(readError)}]`;
        }
        console.log('[APP][CANVAS DEBUG] fetch response', {
          url: requestUrl,
          status: response.status,
          ok: response.ok,
          contentType: response.headers.get('content-type'),
          redirected: response.redirected,
          responseTextPreview,
        });
        return response;
      } catch (error) {
        console.error('[APP][CANVAS DEBUG] fetch error', { url: requestUrl, error });
        throw error;
      }
    };

    window.addEventListener('error', errorListener);
    window.addEventListener('unhandledrejection', rejectionListener);
    console.log('[APP][CANVAS DEBUG] Installed global canvas debug listeners');

    return () => {
      window.fetch = originalFetch;
      window.removeEventListener('error', errorListener);
      window.removeEventListener('unhandledrejection', rejectionListener);
    };
  }, [isSalesforceCanvas]);

  const onRedirectCallback = (appState?: AppState): void => {
    // Mark that a fresh login just occurred so post-login actions can run once
    try {
      localStorage.setItem('justLoggedIn', '1');
    } catch (e) {
      // no-op if localStorage is unavailable
    }

    // Clean up URL parameters after Auth0 has processed the authentication
    cleanupAuthParams();

    const pendingControlLogin = peekMcpControlLogin();
    if (pendingControlLogin) {
      window.location.replace(controlLoginPath(pendingControlLogin));
      return;
    }

    // Always consume the saved path so a stale one cannot leak into a later
    // login. Prefer Auth0 appState.returnTo (TrainingAuthSync / DocumentRedirect),
    // then ForumListContext's localStorage path. Landing routes stay with HomeRoute.
    const fromAppState =
      typeof appState?.returnTo === 'string' ? appState.returnTo : null;
    const redirectPath = localStorage.getItem('redirectPath');
    localStorage.removeItem('redirectPath');
    const target = resolvePostLoginRedirect(
      fromAppState,
      redirectPath,
      window.location.pathname
    );
    if (target) {
      window.location.replace(target);
    }
  };

  // In local mode, we still use Auth0Provider but with dummy values
  // The AppAuthProvider inside will handle local authentication
  const effectiveAuth0Domain = isLocal ? 'login.dev-ept-ai.com' : auth0_domain;
  const effectiveClientId = isLocal ? 'kXTEAzkttuuNt6xoqHaoam2AA5WmZFoz' : client_id;
  // Loopback + remote Auth0 cannot use cross-site iframe silent auth (third-party
  // cookies blocked). Persist tokens like the localhost WebSocket Auth0 client.
  // Hosted apps keep memory cache + same-site silent auth.
  const isLoopback =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  if (isSalesforceCanvas) {
    return (
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <Auth0Context.Provider value={{ ...initialContext, isLoading: false }}>
            <AppContent />
          </Auth0Context.Provider>
          <ReactQueryDevtoolsGate />
        </ThemeProvider>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Auth0Provider
          domain={effectiveAuth0Domain}
          clientId={effectiveClientId}
          authorizationParams={{
            redirect_uri: window.location.origin,
            audience,
            scope: 'openid profile email',
          }}
          cacheLocation={isLoopback ? 'localstorage' : 'memory'}
          useRefreshTokens={isLoopback}
          skipRedirectCallback={mcpClientReturnUrl(window.location.search) !== null}
          onRedirectCallback={onRedirectCallback}
        >
          <AppContent />
        </Auth0Provider>
        <ReactQueryDevtoolsGate />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
